import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { surveyApiService } from '../../api/surveyApi';
import { Card } from '../../components/ui/Card';
import {
    CheckCircleIcon,
    ArrowRightIcon,
    ArrowLeftIcon,
    PaperAirplaneIcon,
    ExclamationCircleIcon,
    GlobeAltIcon,
    CheckIcon,
    SunIcon,
    MoonIcon,
    ClockIcon,
    ListBulletIcon,
    StarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const SurveyFill = () => {
    const { slug } = useParams();
    const [survey, setSurvey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [started, setStarted] = useState(false);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [responses, setResponses] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [lang, setLang] = useState('tr');
    const [isDark, setIsDark] = useState(true);

    useEffect(() => {
        const fetchSurvey = async () => {
            try {
                const data = await surveyApiService.getSurveyDetail(slug);
                setSurvey(data);
                if (data.languages && data.languages.length > 0) {
                    setLang(data.languages[0]);
                }
            } catch (err) {
                console.error('Error fetching survey:', err);
                setError('Anket bulunamadı veya süresi dolmuş.');
            } finally {
                setLoading(false);
            }
        };
        fetchSurvey();
    }, [slug]);

    const handleResponseChange = (questionId, value) => {
        setResponses(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    const validateSection = () => {
        const currentSection = survey.sections[currentSectionIndex];
        if (!currentSection || !currentSection.questions) return true;

        for (const question of currentSection.questions) {
            const value = responses[question.id];
            if (question.is_required) {
                // Check for undefined, null, empty string, or empty array
                if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
                    return false;
                }
            }
        }
        return true;
    };

    const handleNext = () => {
        if (!validateSection()) {
            alert('Lütfen tüm zorunlu soruları doldurun.');
            return;
        }
        if (currentSectionIndex < survey.sections.length - 1) {
            setCurrentSectionIndex(prev => prev + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleBack = () => {
        if (currentSectionIndex > 0) {
            setCurrentSectionIndex(prev => prev - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleSubmit = async () => {
        if (!validateSection()) {
            alert('Lütfen tüm zorunlu soruları doldurun.');
            return;
        }

        setSubmitting(true);
        try {
            // Get all questions to find their types
            const allQuestions = survey.sections.flatMap(s => s.questions);

            const formattedResponses = Object.entries(responses).map(([questionId, value]) => {
                const qId = parseInt(questionId);
                const question = allQuestions.find(q => q.id === qId);
                const type = question?.type_code;

                const response = { question: qId };

                // Choice based questions (radio, checkbox, select, boolean mapping to choices)
                const choiceTypes = ['radio', 'checkbox', 'boolean', 'select', 'dropdown'];
                const hasOptions = (question.options || []).length > 0;

                if (choiceTypes.includes(type) || (hasOptions && ['rating', 'likert_5', 'star_rating'].includes(type))) {
                    // For choice types or numeric types with explicit options, we send to selected_options
                    if (Array.isArray(value)) {
                        response.selected_options = value;
                    } else if (value !== null && value !== undefined && value !== '') {
                        response.selected_options = [value];
                    }

                    // For numeric types that ALSO have options, we also try to send numeric_answer
                    if (['rating', 'likert_5', 'star_rating', 'range_100'].includes(type)) {
                        // If value is numeric, use it directly
                        if (typeof value === 'number') {
                            response.numeric_answer = value;
                        } else if (hasOptions) {
                            // Try to find the weight or value of the selected option
                            const selectedOpt = question.options.find(o => o.id === value);
                            if (selectedOpt) {
                                response.numeric_answer = parseFloat(selectedOpt.weight) || 0;
                            }
                        }
                    }
                } else if (typeof value === 'number' && !['text_short', 'text_long', 'textarea'].includes(type)) {
                    // Purely numeric answers
                    response.numeric_answer = value;
                } else if (value !== null && value !== undefined && value !== '') {
                    // Text answers
                    response.text_answer = String(value);
                }

                return response;
            });

            await surveyApiService.submitSurvey(slug, {
                survey_version: survey.version,
                is_completed: true,
                responses: formattedResponses
            });
            setSubmitted(true);
        } catch (err) {
            console.error('Error submitting survey:', err);
            alert('Gönderim sırasında bir hata oluştu.');
        } finally {
            setSubmitting(false);
        }
    };

    const toggleTheme = () => setIsDark(!isDark);

    const ThemeToggle = () => (
        <button
            onClick={toggleTheme}
            className="fixed top-4 right-4 md:top-8 md:right-8 z-50 p-2.5 md:p-3 rounded-2xl bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-200/20 dark:shadow-none transition-all hover:scale-110 active:scale-95 group"
            title={isDark ? "Aydınlık Moda Geç" : "Karanlık Moda Geç"}
        >
            {isDark ? <SunIcon className="w-4 h-4 md:w-5 md:h-5 text-amber-500 transition-colors group-hover:text-amber-400" /> : <MoonIcon className="w-4 h-4 md:w-5 md:h-5 text-slate-900 transition-colors group-hover:text-indigo-600" />}
        </button>
    );

    if (loading) return (
        <div className={`min-h-screen flex flex-col items-center justify-center font-survey ${isDark ? 'dark bg-[#0F1117]' : 'bg-slate-50'}`}>
            <div
                className="w-16 h-16 border-[4px] border-t-transparent rounded-full animate-spin mb-6"
                style={{ borderColor: 'var(--primary-color)', opacity: 0.2, borderTopColor: 'var(--primary-color)', borderTopOpacity: 1 }}
            />
            <p className="text-slate-400 font-bold text-xs uppercase tracking-widest animate-pulse">Form Yükleniyor</p>
        </div>
    );

    if (error) return (
        <div className={`min-h-screen font-survey ${isDark ? 'dark bg-[#0F1117]' : 'bg-slate-50'}`}>
            <div className="min-h-screen flex items-center justify-center p-4">
                <div className="max-w-md w-full p-10 text-center bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl shadow-slate-200/30 dark:shadow-none border border-slate-100 dark:border-slate-800">
                    <div className="w-20 h-20 bg-rose-50 dark:bg-rose-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <ExclamationCircleIcon className="w-10 h-10 text-rose-500" />
                    </div>
                    <h1 className="text-2xl font-black text-slate-900 dark:text-white mb-3">{error}</h1>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-6 px-8 py-4 text-white rounded-2xl font-bold hover:opacity-90 transition-all shadow-xl active:scale-95"
                        style={{ backgroundColor: 'var(--primary-color)' }}
                    >
                        Yeniden Dene
                    </button>
                </div>
            </div>
        </div>
    );

    if (submitted) return (
        <div className={`min-h-screen font-survey ${isDark ? 'dark bg-[#0F1117]' : 'bg-slate-50'}`}>
            <div className="min-h-screen flex items-center justify-center p-4">
                <div className="max-w-lg w-full text-center p-16 bg-white dark:bg-slate-900 rounded-[3rem] shadow-2xl shadow-slate-200/40 dark:shadow-none border border-slate-100 dark:border-slate-800">
                    <div className="w-24 h-24 bg-emerald-50 dark:bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-10">
                        <CheckCircleIcon className="w-12 h-12 text-emerald-500" />
                    </div>
                    <h1 className="text-4xl font-black text-slate-900 dark:text-white mb-6">Teşekkür Ederiz!</h1>
                    <p className="text-lg text-slate-500 dark:text-slate-400 font-medium leading-relaxed max-w-sm mx-auto">
                        Yanıtlarınız sistemimize başarıyla ulaştı. Değerli vaktinizi ayırdığınız için teşekkürler.
                    </p>
                </div>
            </div>
        </div>
    );

    if (!survey?.sections || survey.sections.length === 0) {
        return (
            <div className={`min-h-screen font-survey ${isDark ? 'dark bg-[#0F1117]' : 'bg-slate-50'}`}>
                <div className="min-h-screen flex items-center justify-center p-4">
                    <div className="max-w-md w-full p-12 text-center bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-xl border border-slate-100 dark:border-slate-800">
                        <ClockIcon className="w-16 h-16 text-amber-500 mx-auto mb-8 opacity-40" />
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">Hazırlık Sürüyor</h1>
                        <p className="text-slate-500 dark:text-slate-400 font-medium">Bu anket üzerinde hala çalışıyoruz. Lütfen daha sonra tekrar ziyaret edin.</p>
                    </div>
                </div>
            </div>
        );
    }

    const primaryColor = survey.primary_color || '#4F46E5';
    const primaryColorLight = primaryColor + '10'; // 6% opacity for a very soft look
    const primaryColorHover = primaryColor + 'cc';

    // Welcome Screen
    if (!started) {
        return (
            <div
                className={`min-h-screen font-survey overflow-x-hidden ${isDark ? 'dark bg-[#0F1117]' : 'bg-white'}`}
                style={{
                    '--primary-color': primaryColor,
                    '--primary-color-light': primaryColorLight,
                    '--primary-color-hover': primaryColorHover
                }}
            >
                <ThemeToggle />
                <div className="min-h-screen flex items-center justify-center p-6 md:p-8">
                    <div className="max-w-3xl w-full">
                        <div className="text-center space-y-10 md:space-y-12">
                            <div className="space-y-6 md:space-y-8">
                                <span
                                    className="inline-flex px-5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] shadow-sm transition-all"
                                    style={{
                                        backgroundColor: 'var(--primary-color-light)',
                                        color: 'var(--primary-color)',
                                    }}
                                >
                                    Resmi Anket Formu
                                </span>
                                <h1 className="text-5xl md:text-7xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
                                    {survey.title?.[lang] || Object.values(survey.title || {})[0]}
                                </h1>
                                {survey.description && (
                                    <p className="text-xl text-slate-500 dark:text-slate-500 max-w-xl mx-auto font-medium leading-relaxed">
                                        {survey.description?.[lang] || Object.values(survey.description || {})[0]}
                                    </p>
                                )}
                            </div>

                            <div className="grid grid-cols-2 md:flex md:flex-wrap justify-center gap-6 md:gap-12 py-8">
                                <div className="flex flex-col items-center space-y-3">
                                    <div className="p-3 md:p-4 bg-slate-50 dark:bg-slate-800/10 rounded-2xl md:rounded-3xl group transition-all hover:scale-105">
                                        <ListBulletIcon className="w-5 h-5 md:w-7 md:h-7 text-slate-500 dark:text-slate-400 group-hover:text-[var(--primary-color)] transition-colors" />
                                    </div>
                                    <div className="text-center">
                                        <p className="text-[8px] md:text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-0.5 md:mb-1">Kapsam</p>
                                        <p className="text-sm md:text-lg font-semibold text-slate-900 dark:text-white">{survey.sections.length} Bölüm</p>
                                    </div>
                                </div>
                                <div className="flex flex-col items-center space-y-3">
                                    <div className="p-3 md:p-4 bg-slate-50 dark:bg-slate-800/10 rounded-2xl md:rounded-3xl group transition-all hover:scale-105">
                                        <ClockIcon className="w-5 h-5 md:w-7 md:h-7 text-slate-500 dark:text-slate-400 group-hover:text-[var(--primary-color)] transition-colors" />
                                    </div>
                                    <div className="text-center">
                                        <p className="text-[8px] md:text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-0.5 md:mb-1">Süre</p>
                                        <p className="text-sm md:text-lg font-semibold text-slate-900 dark:text-white">{survey.estimated_minutes || 5} Dakika</p>
                                    </div>
                                </div>
                                <div className="flex flex-col items-center space-y-3 col-span-2 md:col-span-1">
                                    <div className="p-3 md:p-4 bg-slate-50 dark:bg-slate-800/10 rounded-2xl md:rounded-3xl group transition-all hover:scale-105">
                                        <GlobeAltIcon className="w-5 h-5 md:w-7 md:h-7 text-slate-500 dark:text-slate-400 group-hover:text-[var(--primary-color)] transition-colors" />
                                    </div>
                                    <div className="text-center">
                                        <p className="text-[8px] md:text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-0.5 md:mb-1">Erişim</p>
                                        <p className="text-sm md:text-lg font-semibold text-slate-900 dark:text-white">Herkese Açık</p>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-8">
                                <button
                                    onClick={() => setStarted(true)}
                                    className="px-16 py-6 text-white rounded-3xl font-black text-xl hover:opacity-95 transition-all shadow-2xl active:scale-95 group relative overflow-hidden"
                                    style={{
                                        backgroundColor: 'var(--primary-color)',
                                        boxShadow: `0 25px 50px -12px ${primaryColor}50`
                                    }}
                                >
                                    <span className="relative z-10 flex items-center gap-3">
                                        Başlamak İçin Tıklayın
                                        <ArrowRightIcon className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </button>
                                <p className="mt-8 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                                    Tahmini bitirme süresi {survey.estimated_minutes || 5} dakikadır
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const currentSection = survey.sections[currentSectionIndex];
    if (!currentSection) return null;
    const progress = (survey.sections.length > 0) ? ((currentSectionIndex + 1) / survey.sections.length) * 100 : 0;

    return (
        <div
            className={`min-h-screen font-survey overflow-x-hidden ${isDark ? 'dark bg-[#0F1117]' : 'bg-slate-50'}`}
            style={{
                '--primary-color': primaryColor,
                '--primary-color-light': primaryColorLight
            }}
        >
            <ThemeToggle />

            {/* Header / Premium Progress Bar */}
            <div className="sticky top-0 z-40 bg-white/70 dark:bg-[#0F1117]/70 backdrop-blur-2xl border-b border-slate-200/50 dark:border-slate-800/50 transition-all">
                <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 md:py-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-4">
                        <div className="space-y-0.5 text-center md:text-left">
                            <h2 className="text-xs md:text-base font-bold text-slate-900 dark:text-white leading-tight tracking-tight px-2 md:px-0">
                                {currentSection.title?.[lang] || Object.values(currentSection.title || {})[0]}
                            </h2>
                            <p
                                className="text-[8px] md:text-[9px] font-black uppercase tracking-[0.2em]"
                                style={{ color: 'var(--primary-color)' }}
                            >
                                Bölüm {currentSectionIndex + 1} / {survey.sections.length}
                            </p>
                        </div>
                        <div className="flex items-center justify-center md:justify-end gap-3 md:gap-4">
                            <div className="flex flex-col items-end gap-1">
                                <div className="w-32 md:w-48 h-1.5 bg-slate-200/50 dark:bg-slate-800/50 rounded-full overflow-hidden">
                                    <div
                                        className="h-full transition-all duration-1000 ease-out shadow-[0_0_8px_var(--primary-color)]"
                                        style={{ width: `${progress}%`, backgroundColor: 'var(--primary-color)' }}
                                    />
                                </div>
                                <span className="text-[9px] font-bold text-slate-600 dark:text-slate-500 uppercase tracking-widest">
                                    Tamamlanma: %{Math.round(progress)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <main className="max-w-2xl mx-auto px-4 md:px-6 pt-10 md:pt-16 pb-32">
                <div className="space-y-20">
                    {currentSection.questions && currentSection.questions.map((question, qIdx) => (
                        <div key={question.id} className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <span
                                        className="w-8 h-8 flex items-center justify-center bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded-lg text-xs font-bold border border-slate-100 dark:border-slate-800 shadow-sm transition-transform"
                                        style={{ color: 'var(--primary-color)' }}
                                    >
                                        {qIdx + 1}
                                    </span>
                                    {question.is_required && (
                                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800/40 border border-slate-200/50 dark:border-slate-700/50">
                                            <div className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: 'var(--primary-color)' }}></div>
                                            <span className="text-[9px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tighter">
                                                Zorunlu
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <h4 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white leading-tight tracking-tight">
                                    {question.title?.[lang] || Object.values(question.title || {})[0]}
                                </h4>
                                {question.help_text && (
                                    <p className="text-base text-slate-500 dark:text-slate-500 leading-relaxed font-medium">
                                        {question.help_text?.[lang] || Object.values(question.help_text || {})[0]}
                                    </p>
                                )}
                            </div>

                            <div className="bg-transparent">
                                {renderQuestionInput(question, responses[question.id], (val) => handleResponseChange(question.id, val), lang, isDark, primaryColor)}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Soft Navigation */}
                <div className="mt-16 md:mt-32 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 border-t border-slate-200/50 dark:border-slate-800/50 pt-10 md:pt-16">
                    <button
                        onClick={handleBack}
                        disabled={currentSectionIndex === 0}
                        className={`group w-full md:w-auto flex items-center justify-center space-x-2 px-6 py-4 rounded-2xl font-bold text-xs transition-all active:scale-95 ${currentSectionIndex === 0
                            ? 'hidden md:flex text-slate-200 dark:text-slate-800 cursor-not-allowed opacity-30 shadow-none'
                            : 'text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:text-slate-900 dark:hover:text-white shadow-sm'
                            }`}
                    >
                        <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                        <span>GERİ DÖN</span>
                    </button>

                    {currentSectionIndex < survey.sections.length - 1 ? (
                        <button
                            onClick={handleNext}
                            className="w-full md:w-auto flex items-center justify-center space-x-3 px-8 py-4 bg-slate-900 dark:bg-white dark:text-slate-900 text-white rounded-2xl font-bold text-sm hover:opacity-90 transition-all shadow-xl active:scale-95 order-first md:order-last"
                        >
                            <span>SONRAKİ ADIM</span>
                            <ArrowRightIcon className="w-5 h-5" />
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="w-full md:w-auto flex items-center justify-center space-x-4 px-10 py-5 text-white rounded-2xl font-bold text-base hover:opacity-95 transition-all shadow-xl active:scale-95 disabled:opacity-50 group order-first md:order-last"
                            style={{
                                backgroundColor: 'var(--primary-color)',
                                boxShadow: `0 20px 40px -12px ${primaryColor}50`
                            }}
                        >
                            {submitting ? (
                                <div className="w-5 h-5 border-4 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <PaperAirplaneIcon className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                            )}
                            <span>{submitting ? 'Gönderiliyor' : 'ANKETİ BİTİR'}</span>
                        </button>
                    )}
                </div>
            </main>
        </div>
    );
};

const renderQuestionInput = (question, value, onChange, lang, isDark, primaryColor) => {
    const type = question.type_code;
    const baseInputClass = "w-full bg-white dark:bg-slate-900/40 border-[1.5px] border-slate-200 dark:border-slate-800/80 focus:border-[var(--primary-color)] dark:focus:border-[var(--primary-color)] rounded-2xl px-6 py-4 text-base font-semibold outline-none transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 shadow-sm focus:shadow-xl focus:shadow-[var(--primary-color-light)]";

    switch (type) {
        case 'text_short':
        case 'text':
        case 'email':
        case 'url':
        case 'number':
        case 'date':
        case 'phone':
            return (
                <input
                    type={type === 'text_short' ? 'text' : (type === 'phone' ? 'tel' : type)}
                    className={baseInputClass}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Yanıtınızı giriniz..."
                />
            );
        case 'text_long':
        case 'textarea':
            return (
                <textarea
                    rows="6"
                    className={`${baseInputClass} resize-none`}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Yanıtınızı buraya detaylandırabilirsiniz..."
                />
            );
        case 'radio':
        case 'boolean':
            let options = question.options || [];
            if (type === 'boolean' && options.length === 0) {
                options = [
                    { id: 'yes', text: { tr: 'Evet', en: 'Yes', ru: 'Да', de: 'Ja' } },
                    { id: 'no', text: { tr: 'Hayır', en: 'No', ru: 'Нет', de: 'Nein' } }
                ];
            }

            return (
                <div className="grid grid-cols-1 gap-4">
                    {options.map(option => (
                        <button
                            key={option.id}
                            onClick={() => onChange(option.id)}
                            className={`flex items-center justify-between p-5 rounded-2xl border-[1.5px] transition-all text-left group overflow-hidden relative ${value === option.id
                                ? 'border-[var(--primary-color)] bg-[var(--primary-color-light)] shadow-lg shadow-[var(--primary-color-light)]'
                                : 'border-slate-200 dark:border-slate-800/80 bg-white dark:bg-slate-900/10 hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-md'
                                }`}
                        >
                            <span className={`text-base font-bold ${value === option.id ? 'text-[var(--primary-color)]' : 'text-slate-700 dark:text-slate-300'}`}>
                                {option.text?.[lang] || Object.values(option.text || {})[0]}
                            </span>
                            <div
                                className={`w-6 h-6 rounded-xl border-[1.5px] flex items-center justify-center transition-all ${value === option.id ? 'bg-[var(--primary-color)] rotate-0 scale-100 shadow-lg' : 'border-slate-200 dark:border-slate-700 rotate-90 scale-75'
                                    }`}
                                style={{ borderColor: value === option.id ? 'var(--primary-color)' : '' }}
                            >
                                {value === option.id && <CheckIcon className="w-4 h-4 text-white stroke-[4]" />}
                            </div>
                        </button>
                    ))}
                </div>
            );
        case 'checkbox':
            const currentValues = value || [];
            return (
                <div className="grid grid-cols-1 gap-4">
                    {(question.options || []).map(option => {
                        const isChecked = currentValues.includes(option.id);
                        return (
                            <button
                                key={option.id}
                                onClick={() => {
                                    const newVal = isChecked
                                        ? currentValues.filter(v => v !== option.id)
                                        : [...currentValues, option.id];
                                    onChange(newVal);
                                }}
                                className={`flex items-center justify-between p-5 rounded-2xl border-[1.5px] transition-all text-left ${isChecked
                                    ? 'border-[var(--primary-color)] bg-[var(--primary-color-light)] shadow-lg shadow-[var(--primary-color-light)]'
                                    : 'border-slate-200 dark:border-slate-800/80 bg-white dark:bg-slate-900/10 hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-md'
                                    }`}
                            >
                                <span className={`text-base font-bold ${isChecked ? 'text-[var(--primary-color)]' : 'text-slate-700 dark:text-slate-300'}`}>
                                    {option.text?.[lang] || Object.values(option.text || {})[0]}
                                </span>
                                <div
                                    className={`w-6 h-6 rounded-lg border-[1.5px] flex items-center justify-center transition-all ${isChecked ? 'bg-[var(--primary-color)] scale-100 shadow-lg' : 'border-slate-200 dark:border-slate-700 scale-75'
                                        }`}
                                    style={{ borderColor: isChecked ? 'var(--primary-color)' : '' }}
                                >
                                    {isChecked && <CheckIcon className="w-4 h-4 text-white stroke-[4]" />}
                                </div>
                            </button>
                        );
                    })}
                </div>
            );
        case 'range_100':
            return (
                <div className="space-y-8 py-8 px-6 bg-white dark:bg-slate-900/20 rounded-3xl border-[1.5px] border-slate-200 dark:border-slate-800/50 shadow-sm">
                    <div className="flex justify-between items-center">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Minimal</span>
                        <div className="text-center group flex flex-col items-center">
                            <div className="relative group-hover:scale-110 transition-transform duration-500">
                                <span
                                    className="text-6xl font-black tracking-tighter block"
                                    style={{ color: 'var(--primary-color)' }}
                                >
                                    {value || 0}
                                </span>
                            </div>
                            <span className="text-[9px] font-black text-slate-500 dark:text-slate-500 tracking-[0.3em]">PUAN</span>
                        </div>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Maksimal</span>
                    </div>
                    <div className="px-4">
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={value || 0}
                            onChange={(e) => onChange(parseInt(e.target.value))}
                            className="w-full h-4 bg-slate-100 dark:bg-slate-800 rounded-full appearance-none cursor-pointer accent-[var(--primary-color)]"
                        />
                    </div>
                </div>
            );
        case 'rating':
        case 'likert_5':
            const hasExplicitOptions = (question.options || []).length > 0;
            const max = type === 'likert_5' ? 5 : 10;
            const scaleItems = hasExplicitOptions
                ? question.options
                : Array.from({ length: max }, (_, i) => ({ id: i + 1, text: null, val: i + 1 }));

            return (
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                    {scaleItems.map((item, idx) => {
                        const isSelected = value === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => onChange(item.id)}
                                className={`min-w-[56px] h-14 md:h-16 px-4 rounded-2xl border-[1.5px] font-bold text-lg transition-all flex items-center justify-center ${isSelected
                                    ? 'text-white scale-110 shadow-xl'
                                    : 'border-slate-200 dark:border-slate-800/50 bg-white dark:bg-slate-900/40 text-slate-700 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600 shadow-sm'
                                    }`}
                                style={{
                                    backgroundColor: isSelected ? 'var(--primary-color)' : '',
                                    borderColor: isSelected ? 'var(--primary-color)' : '',
                                    boxShadow: isSelected ? `0 10px 20px -5px ${primaryColor}60` : ''
                                }}
                            >
                                {item.text ? (item.text?.[lang] || Object.values(item.text || {})[0]) : (item.val || idx + 1)}
                            </button>
                        );
                    })}
                </div>
            );
        case 'star_rating':
            return (
                <div className="flex items-center space-x-3 py-4 justify-center md:justify-start">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            onClick={() => onChange(star)}
                            className="group transition-all active:scale-95 hover:scale-110 focus:outline-none"
                        >
                            <div className="relative">
                                {value >= star ? (
                                    <StarIconSolid className="w-12 h-12 text-amber-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.3)] transition-all transform animate-in zoom-in-75" />
                                ) : (
                                    <div className="group-hover:bg-slate-50 dark:group-hover:bg-slate-800 p-1 rounded-full transition-colors">
                                        <StarIcon className="w-10 h-10 text-slate-300 dark:text-slate-700 group-hover:text-amber-400/50 transition-colors stroke-[1.5]" />
                                    </div>
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            );
        default:
            return (
                <div className="p-16 bg-slate-50 dark:bg-slate-900/30 rounded-[3rem] border-2 border-dashed border-slate-100 dark:border-slate-800 text-slate-400 text-sm font-bold uppercase tracking-widest text-center italic opacity-50">
                    Modül Hazırlanıyor ({type})
                </div>
            );
    }
};

export default SurveyFill;
